import dayjs from "dayjs";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../theme/components/common/PageHeader";
import * as React from "react";
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DatePicker from "react-datepicker";
import { getDatePickerLocaleProps } from "utils/dateLocales";
import { Link } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Pagination from "react-bootstrap/Pagination";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import FormattedFormControl from "components/form/FormattedFormControl";
import apiClient from "../api";
import { GetUnits_unit } from "../utils/__generated__/GetUnits";
import { useSortedUnits } from "../hooks/units";
import {
  GET_MANUAL_POINT_SETS,
  GET_MANUAL_POINT_SETS_COMBINED,
  GET_MANUAL_POINT_SET_BY_ID,
} from "../utils/stations";
import { getFormValueHandler } from "../utils/forms";
import { gettext } from "../i18n";
import {
  GetManualPointSetsQuery,
  GetManualPointSetsQueryVariables,
  GetManualPointSetsQuery_manual_point_set as ManualPointSetSingle,
} from "../utils/__generated__/GetManualPointSetsQuery";
import {
  GetManualPointSetsCombined,
  GetManualPointSetsCombinedVariables,
  GetManualPointSetsCombined_manual_point_set as ManualPointSetCombined,
} from "../utils/__generated__/GetManualPointSetsCombined";
import {
  GetManualPointSetById,
  GetManualPointSetByIdVariables,
  GetManualPointSetById_manual_point_set,
  GetManualPointSetById_manual_point_set_datapoints,
} from "../utils/__generated__/GetManualPointSetById";
import BackToLink from "components/common/BackToLink";
import PermissionContextCheck from "components/auth/PermissionContextCheck";
import DefaultTooltip from "components/common/DefaultTooltip";
import SessionTimeout from "components/common/SessionTimeout";
import { parseNumericFormValues, formatNumberLocale } from "utils/formatting";
import {
  onInputFileChange,
  parseExcelFormat,
  groupDataForAPI,
  getTotalDatapointsCount,
  GroupedStationData,
} from "utils/xlsx";
import { useAuth } from "state/auth";
import "./MeasurementStationManualDatapointsPage.scss";

type ManualPointSet =
  | ManualPointSetSingle
  | ManualPointSetCombined
  | GetManualPointSetById_manual_point_set;

type EditableDatapoint = GetManualPointSetById_manual_point_set_datapoints;

const MANUAL_SETS_PER_PAGE = 25;

interface MeasurementStationManualDatapointsPageProps {
  combined?: boolean;
  editDatapoints?: boolean;
}

const MeasurementStationManualDatapointsPage = ({
  combined = false,
  editDatapoints = false,
}: MeasurementStationManualDatapointsPageProps) => {
  const { identifier, datapointSetId } = useParams();

  const navigate = useNavigate();
  const [stationQuery, setStationQuery] = React.useState("");
  const [debouncedStationQuery, setDebouncedStationQuery] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const debouncedSetQuery = React.useMemo(
    () => debounce((value: string) => setDebouncedStationQuery(value), 400),
    [],
  );

  React.useEffect(() => {
    debouncedSetQuery(stationQuery);
    setCurrentPage(1);
    return () => debouncedSetQuery.cancel();
  }, [stationQuery, debouncedSetQuery]);

  const createDatapointSet = datapointSetId === "new";
  const editDatapointSet = !createDatapointSet && !!datapointSetId;
  const editingSetId = editDatapointSet ? parseInt(datapointSetId, 10) : null;

  const {
    data: listData,
    error: listError,
    refetch: datapointSetRefetch,
  } = useQuery<
    GetManualPointSetsQuery | GetManualPointSetsCombined,
    GetManualPointSetsQueryVariables | GetManualPointSetsCombinedVariables
  >(combined ? GET_MANUAL_POINT_SETS_COMBINED : GET_MANUAL_POINT_SETS, {
    variables: {
      ...(combined
        ? { searchQuery: `%${debouncedStationQuery}%` }
        : { identifier }),
      limit: MANUAL_SETS_PER_PAGE,
      offset: (currentPage - 1) * MANUAL_SETS_PER_PAGE,
    },
    skip: !!datapointSetId,
  });

  const {
    data: editData,
    error: editError,
    loading: editSetLoading,
    refetch: editSetRefetch,
  } = useQuery<GetManualPointSetById, GetManualPointSetByIdVariables>(
    GET_MANUAL_POINT_SET_BY_ID,
    {
      variables: { id: editingSetId as number },
      skip: !editingSetId || Number.isNaN(editingSetId),
    },
  );

  const [savingUploadedValues, setSavingUploadedValues] =
    React.useState<boolean>(false);
  const [uploadedValues, setUploadedValues] =
    React.useState<GroupedStationData>({});

  const uploadDatapoints = () => {
    if (
      !uploadedValues ||
      Object.keys(uploadedValues).length === 0 ||
      savingUploadedValues
    ) {
      return;
    }

    // Get first key as station identifier
    const stationIdentifier = Object.keys(uploadedValues)[0];
    if (!stationIdentifier) {
      toast.error("No station identifier found in uploaded data.");
      return;
    }

    setSavingUploadedValues(true);
    const requestPath = "/organization-admin/upload-manual-datapoints";
    apiClient
      .request(requestPath, {
        method: "POST",
        data: {
          stationIdentifier,
          groupedDatapoints: uploadedValues,
        },
      })
      .then(() => {
        toast.success(gettext("Success"));
        setUploadedValues({});
        datapointSetRefetch();
        setSavingUploadedValues(false);
      })
      .catch((e) => {
        toast.error(e.message.toString());
        setSavingUploadedValues(false);
        datapointSetRefetch();
      });
  };

  if (!datapointSetId) {
    if (listError) {
      console.error(JSON.stringify(listError, null, 2));
      return <SessionTimeout />;
    }
    if (!listData || (!combined && !identifier)) {
      return <div />;
    }
  }
  if (editDatapointSet) {
    if (editError) {
      console.error(JSON.stringify(editError, null, 2));
      return <SessionTimeout />;
    }
    if (
      !editSetLoading &&
      (!editData || editData.manual_point_set.length === 0)
    ) {
      return <div>Datapoint set not found</div>;
    }
  }

  const pointSets = listData?.manual_point_set || [];
  const datapointSetToEdit = editDatapointSet
    ? editData?.manual_point_set?.[0]
    : undefined;
  const totalCount =
    listData?.manual_point_set_aggregate?.aggregate?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / MANUAL_SETS_PER_PAGE));

  const onFileLoaded = (data: string[][]) => {
    if (!data || data.length === 0) {
      toast.error("No data found in the uploaded file");
      return;
    }

    console.log("Raw file data:", data);

    try {
      const processedData = parseExcelFormat(data);
      console.log("Processed data:", processedData);

      const groupedData = groupDataForAPI(processedData);
      console.log("Grouped data:", groupedData);

      setUploadedValues(groupedData);

      if (Object.keys(groupedData).length > 0) {
        const stationCount = Object.keys(groupedData).length;
        const totalDatapoints = getTotalDatapointsCount(groupedData);

        toast.success(
          `Parsed ${totalDatapoints} datapoints from ${stationCount} station${
            stationCount > 1 ? "s" : ""
          }`,
        );
      } else {
        toast.warning(
          "No valid datapoints found in the uploaded file. Please check the format.",
        );
      }
    } catch (error) {
      console.error("Error parsing Excel data:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Please check the format.";
      toast.error(`Error parsing file: ${errorMessage}`);
    }
  };

  const datapointSetsPath = identifier
    ? `/stations/${identifier}/manual-datapoints`
    : "/stations/manual-datapoints";

  return (
    <div className="MeasurementStationManualDatapointsPage">
      {!!identifier && (
        <BackToLink to={`/stations/${identifier}`}>
          Back to measurement station.
        </BackToLink>
      )}
      <PageHeader
        title={gettext("Manual/lab data")}
        className="mb-3"
      ></PageHeader>
      <Card className="mb-3">
        <Card.Body>
          {editDatapoints && datapointSetToEdit && (
            <div>
              <BackToLink to={datapointSetsPath}>
                Back to datapoint sets table
              </BackToLink>
              <h4>Edit datapoints of: {datapointSetToEdit.name}</h4>
              <DatapointsEditTable
                datapointSet={datapointSetToEdit}
                datapointSetRefetch={editSetRefetch}
              />
            </div>
          )}
          {!editDatapoints && (datapointSetToEdit || createDatapointSet) && (
            <div>
              <BackToLink to={datapointSetsPath}>
                Back to datapoint sets table
              </BackToLink>
              {datapointSetToEdit ? (
                <h4>Edit datapoint set: {datapointSetToEdit.name}</h4>
              ) : (
                <h4>Create manual/lab data</h4>
              )}
              <DatapointSetUpdateForm
                identifier={identifier}
                datapointSet={datapointSetToEdit}
                onSuccess={() => {
                  navigate(datapointSetsPath);
                }}
              />
            </div>
          )}
          {!datapointSetId && !savingUploadedValues && (
            <>
              {!identifier && (
                <div className="mb-3">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
                    <span className="fw-semibold">
                      Upload datapoints from CSV or XLSX file
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <a href="/xlsx/example_file_for_import.xlsx">
                        <Button size="sm">Download Template</Button>
                      </a>
                      <DefaultTooltip
                        popHeader={gettext("Template File")}
                        popBody={
                          <p>
                            Use the Excel template with Station ID (A),
                            Timestamp (B), and multiple channel columns (C+)
                            with units in row 2 and values in rows 3+.
                          </p>
                        }
                      />
                    </div>
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="file"
                      name="file-upload-input"
                      accept=".csv, .xlsx"
                      onChange={onInputFileChange(onFileLoaded)}
                      size="sm"
                    />
                  </Form.Group>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={
                      savingUploadedValues ||
                      Object.keys(uploadedValues).length === 0
                    }
                    onClick={(e) => {
                      e.preventDefault();
                      uploadDatapoints();
                    }}
                  >
                    Upload ({getTotalDatapointsCount(uploadedValues)}{" "}
                    datapoints)
                  </Button>
                </div>
              )}
              {combined && (
                <Row className="g-3 mb-3">
                  <Col xs={12} lg={6}>
                    <Form.Label className="fw-semibold">Station</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={gettext(
                        "Search by station name, identifier, organization or data set",
                      )}
                      value={stationQuery}
                      onChange={(e) => setStationQuery(e.target.value)}
                    />
                  </Col>
                </Row>
              )}
              <DatapointSetTable
                identifier={identifier}
                combined={combined}
                datapointSets={pointSets}
                onSuccess={() => {
                  datapointSetRefetch().catch(console.error);
                }}
              />
              {totalPages > 1 && (
                <Pagination className="justify-content-center">
                  <Pagination.Prev
                    onClick={() =>
                      setCurrentPage((page) => Math.max(1, page - 1))
                    }
                    disabled={currentPage === 1}
                  >
                    {gettext("Previous")}
                  </Pagination.Prev>
                  <Pagination.Item disabled>
                    {gettext("Page")} {currentPage} {gettext("of")} {totalPages}
                  </Pagination.Item>
                  <Pagination.Next
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    {gettext("Next")}
                  </Pagination.Next>
                </Pagination>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

interface DatapointSetTableProps {
  datapointSets: ManualPointSet[];
  onSuccess?: () => void;
  combined?: boolean;
  identifier?: string;
}
const DatapointSetTable = ({
  identifier,
  datapointSets,
  onSuccess,
  combined = false,
}: DatapointSetTableProps) => {
  const { user } = useAuth();
  const isAdmin = !!user?.admin;

  const deleteHandler = (sourceUUID: string) => (e: any) => {
    e.preventDefault();
    const answer = window.confirm(
      gettext(
        "Are you sure you want to delete this datapoint set? This cannot be undone.",
      ),
    );
    if (answer) {
      apiClient
        .request("/organization-member/delete-manual-point-set", {
          method: "DELETE",
          data: {
            source_uuid: sourceUUID,
          },
        })
        .then(() => {
          toast.success(gettext("Datapoint set deleted successfully"));
          if (onSuccess) {
            onSuccess();
          }
        })
        .catch((e) => {
          toast.error("Error deleting datapoint set: " + e.toString());
        });
    }
  };
  return (
    <div>
      <div className="d-none d-md-block overflow-auto">
        <Table style={{ tableLayout: "auto" }}>
          <thead>
            <tr>
              <th colSpan={combined ? 6 : 5}>Existing data sets</th>
            </tr>
            <tr>
              {combined && <th style={{ width: "15%" }}>Organization</th>}
              <th style={{ width: "10%" }}>Station</th>
              <th style={{ width: "15%" }}>Identifier</th>
              <th style={{ width: "25%" }}>Name</th>
              <th style={{ width: "8%" }}>Unit</th>
              <th style={{ width: "auto" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {datapointSets.map((datapointSet) => {
              let formulaUnit = datapointSet.unit?.symbol;
              if (formulaUnit === undefined) {
                formulaUnit = gettext("(unassigned)");
              } else if (formulaUnit === "") {
                formulaUnit = gettext("(no unit)");
              }
              return (
                <tr key={datapointSet.id}>
                  {combined && (
                    <td className="text-break">
                      {(datapointSet as ManualPointSetCombined).station
                        .organization?.name ?? "—"}
                    </td>
                  )}
                  <td className="text-break">
                    {datapointSet.station.identifier}
                  </td>
                  <td className="text-break">{datapointSet.identifier}</td>
                  <td className="text-break">{datapointSet.name}</td>
                  <td>{formulaUnit}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <PermissionContextCheck
                        category="source_edit"
                        requiredPermission="add_manual_datapoint"
                        target={!isAdmin && datapointSet.source_uuid}
                      >
                        <>
                          <Button
                            as={Link as any}
                            to={`/stations/${
                              combined
                                ? ""
                                : `${datapointSet.station.identifier}/`
                            }manual-datapoints/${datapointSet.id}`}
                            variant="outline-primary"
                            size="sm"
                          >
                            Edit
                          </Button>
                          <Button
                            as={Link as any}
                            to={`/stations/${
                              combined
                                ? ""
                                : `${datapointSet.station.identifier}/`
                            }manual-datapoints/${datapointSet.id}/edit-datapoints`}
                            variant="outline-primary"
                            size="sm"
                          >
                            Add Results
                          </Button>
                        </>
                      </PermissionContextCheck>
                      <PermissionContextCheck
                        category="source_edit"
                        requiredPermission="delete_manual_point_set"
                        target={!isAdmin && datapointSet.source_uuid}
                      >
                        <Button
                          type="button"
                          variant="outline-danger"
                          size="sm"
                          onClick={deleteHandler(datapointSet.source_uuid)}
                        >
                          Delete
                        </Button>
                      </PermissionContextCheck>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="d-md-none">
        {datapointSets.map((datapointSet) => {
          let formulaUnit = datapointSet.unit?.symbol;
          if (formulaUnit === undefined) {
            formulaUnit = gettext("(unassigned)");
          } else if (formulaUnit === "") {
            formulaUnit = gettext("(no unit)");
          }
          return (
            <Card key={datapointSet.id} className="mb-2">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    {combined && (
                      <div className="text-muted small">
                        {(datapointSet as ManualPointSetCombined).station
                          .organization?.name ?? "—"}
                      </div>
                    )}
                    <div className="fw-semibold">
                      {datapointSet.station.identifier}
                    </div>
                    <div className="small text-muted">{datapointSet.name}</div>
                  </div>
                  <span className="badge bg-secondary">{formulaUnit}</span>
                </div>
                <div className="d-flex flex-wrap gap-1">
                  <PermissionContextCheck
                    category="source_edit"
                    requiredPermission="add_manual_datapoint"
                    target={!isAdmin && datapointSet.source_uuid}
                  >
                    <>
                      <Button
                        as={Link as any}
                        to={`/stations/${
                          combined ? "" : `${datapointSet.station.identifier}/`
                        }manual-datapoints/${datapointSet.id}`}
                        variant="outline-primary"
                        size="sm"
                      >
                        Edit
                      </Button>
                      <Button
                        as={Link as any}
                        to={`/stations/${
                          combined ? "" : `${datapointSet.station.identifier}/`
                        }manual-datapoints/${datapointSet.id}/edit-datapoints`}
                        variant="outline-primary"
                        size="sm"
                      >
                        Add Results
                      </Button>
                    </>
                  </PermissionContextCheck>
                  <PermissionContextCheck
                    category="source_edit"
                    requiredPermission="delete_manual_point_set"
                    target={!isAdmin && datapointSet.source_uuid}
                  >
                    <Button
                      type="button"
                      variant="outline-danger"
                      size="sm"
                      onClick={deleteHandler(datapointSet.source_uuid)}
                    >
                      Delete
                    </Button>
                  </PermissionContextCheck>
                </div>
              </Card.Body>
            </Card>
          );
        })}
      </div>

      {!!identifier && (
        <div className="my-2">
          <Button
            as={Link as any}
            to={`/stations/${identifier}/manual-datapoints/new`}
            variant="outline-primary"
            size="sm"
          >
            Create manual/lab data set
          </Button>
        </div>
      )}
    </div>
  );
};

interface DatapointsEditTableProps {
  datapointSet: GetManualPointSetById_manual_point_set | undefined;
  onSuccess?: () => void;
  datapointSetRefetch: () => Promise<any>;
}
const DatapointsEditTable = ({
  datapointSet,
  datapointSetRefetch,
}: DatapointsEditTableProps) => {
  const [editedValues, setEditedValues] = React.useState<EditableDatapoint>();
  const [submittingForm, setSubmittingForm] = React.useState<boolean>(false);
  const [datapointValues, setDatapointValues] = React.useState<Record<
    string,
    any
  > | null>(null);
  const stationIdentifier = datapointSet?.station.identifier;

  const datapoints = React.useMemo(
    () => datapointSet?.datapoints,
    [datapointSet],
  );

  const setValueHandler = getFormValueHandler(
    editedValues as Record<string, any>,
    setEditedValues as (
      value: React.SetStateAction<Record<string, any>>,
    ) => void,
  );

  const setDatapointValue = (key: string, value: any) => {
    setDatapointValues({
      ...datapointValues,
      [key]: value,
    });
  };

  const onSaveEdited = () => {
    if (!editedValues) {
      return;
    }
    const values = editedValues;
    const requestPath = `/organization-member/manual-datapoint/${values.id}`;

    apiClient
      .request(requestPath, {
        method: "PUT",
        data: {
          stationIdentifier,
          source_uuid: datapointSet?.source_uuid,
          ...values,
        },
      })
      .then(() => {
        toast.success("Success");
        setDatapointValues({});
        datapointSetRefetch();
        setEditedValues(undefined);
      })
      .catch((e) => {
        toast.error(e.message.toString());
      });
  };

  const saveNewDatapoint = () => {
    if (!datapointValues) {
      return;
    }
    setSubmittingForm(true);
    const values = parseNumericFormValues({ ...datapointValues });
    const requestPath = "/organization-member/create-manual-datapoint";
    apiClient
      .request(requestPath, {
        method: "POST",
        data: {
          stationIdentifier,
          source_uuid: datapointSet?.source_uuid,
          ...values,
        },
      })
      .then(() => {
        toast.success("Success");
        setDatapointValues({});
        setSubmittingForm(false);
        datapointSetRefetch();
      })
      .catch((e) => {
        toast.error(e.message.toString());
        setSubmittingForm(false);
      });
  };

  const onSave = () => {
    saveNewDatapoint();
  };

  const onDeleteDatapoint = (id: number) => {
    if (
      !window.confirm(
        gettext("Are you sure you want to delete this datapoint?"),
      )
    ) {
      return;
    }
    const requestPath = `/organization-member/manual-datapoint/${id}`;
    apiClient
      .request(requestPath, {
        method: "DELETE",
        data: {
          stationIdentifier,
        },
      })
      .then(() => {
        toast.success("Success");
        datapointSetRefetch();
      })
      .catch((e) => {
        toast.error(e.message.toString());
      });
  };

  return (
    <>
      <Table responsive>
        <thead>
          <tr>
            <th colSpan={4}>Add Datapoint</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>
              <Row>
                <Col>Timestamp</Col>
              </Row>
            </th>
            <td>
              <Form.Group>
                <DatePicker
                  selected={
                    datapointValues?.timestamp
                      ? new Date(datapointValues?.timestamp)
                      : null
                  }
                  showTimeSelect
                  onChange={(d) => {
                    if (!d) {
                      return;
                    }
                    setDatapointValue("timestamp", d);
                  }}
                  dateFormat="dd.MM.yyyy HH:mm"
                  className="form-control my-1"
                  {...getDatePickerLocaleProps()}
                />
              </Form.Group>
            </td>
          </tr>
          <tr>
            <th>
              <Row>
                <Col>Value</Col>
              </Row>
            </th>
            <td>
              <Form.Group>
                <FormattedFormControl
                  type="number"
                  value={datapointValues?.value ?? ""}
                  onChange={(e) => setDatapointValue("value", e.target.value)}
                />
              </Form.Group>
            </td>
          </tr>
        </tbody>
      </Table>
      <div className="sectionTables">
        <Table responsive>
          <tbody>
            <tr>
              <td colSpan={2}>
                <Button
                  type="button"
                  variant="primary"
                  disabled={submittingForm}
                  onClick={(e) => {
                    e.preventDefault();
                    onSave();
                  }}
                >
                  Add Datapoint
                </Button>
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
      {!!datapoints && (
        <div className="sectionTables">
          <Table responsive>
            <thead>
              <tr>
                <th colSpan={4}>Existing Datapoints</th>
              </tr>
              <tr>
                <th>Timestamp</th>
                <th>Value</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {datapoints.map((datapoint: EditableDatapoint) => (
                <tr key={`datapoint_${datapoint.id}`}>
                  {!!editedValues && editedValues?.id === datapoint.id ? (
                    <>
                      <td>
                        <Form.Group>
                          <DatePicker
                            selected={
                              editedValues?.timestamp
                                ? new Date(editedValues?.timestamp)
                                : null
                            }
                            showTimeSelect
                            onChange={(d) => {
                              if (!d) {
                                return;
                              }

                              setEditedValues({
                                ...datapoint,
                                timestamp: d,
                              });
                            }}
                            dateFormat="dd.MM.yyyy HH:mm"
                            className="form-control my-1"
                            {...getDatePickerLocaleProps()}
                          />
                        </Form.Group>
                      </td>
                      <td>
                        <FormattedFormControl
                          type="number"
                          value={editedValues.value}
                          onChange={setValueHandler("value")}
                        />
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        {dayjs(datapoint.timestamp).format("DD.MM.YYYY HH:mm")}
                      </td>
                      <td>
                        {datapoint.raw_value ? (
                          <>
                            <span>{datapoint.raw_value}*</span>{" "}
                            <DefaultTooltip
                              popHeader={gettext("Detection limit value")}
                              popBody={
                                <p>
                                  {gettext(
                                    "The original result was a detection limit value. The stored value is derived from it (50% of the limit for below-detection-limit results).",
                                  )}
                                </p>
                              }
                            />
                          </>
                        ) : (
                          formatNumberLocale(datapoint.value)
                        )}
                      </td>
                    </>
                  )}

                  <td></td>
                  <td className="alignRight">
                    {editedValues?.id === datapoint.id ? (
                      <>
                        <Button size="sm" onClick={onSaveEdited}>
                          Save
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setEditedValues(undefined);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          setEditedValues({
                            ...datapoint,
                          });
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        onDeleteDatapoint(datapoint.id);
                      }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
};

interface DatapointSetUpdateFormProps {
  datapointSet: ManualPointSet | undefined;
  onSuccess?: () => void;
  identifier?: string;
}

const DatapointSetUpdateForm = ({
  datapointSet,
  onSuccess,
  identifier,
}: DatapointSetUpdateFormProps) => {
  const { data: sortedUnits } = useSortedUnits();
  const [formState, setFormState] = React.useState({
    name: datapointSet?.name ?? "",
    systemName: datapointSet?.system_name ?? "",
    sourceIdentifier: datapointSet?.identifier ?? "",
    unitId: datapointSet?.unit?.id,
  });
  const [submittingForm, setSubmittingForm] = React.useState<boolean>(false);

  const stationIdentifier = datapointSet?.station.identifier ?? identifier;
  const creating = !datapointSet;
  let submitDisabled =
    !formState.name || !formState.sourceIdentifier || submittingForm;

  const onSave = () => {
    submitForm();
  };

  const submitForm = () => {
    if (submitDisabled) {
      return;
    }
    setSubmittingForm(true);
    const data = {
      stationIdentifier,
      source_uuid: datapointSet?.source_uuid,
      ...formState,
    };

    const path = creating
      ? "/organization-member/create-manual-point-set"
      : "/organization-member/edit-manual-point-set";
    apiClient
      .request(path, {
        method: "POST",
        data,
      })
      .then(() => {
        toast.success(gettext(`Datapoint set updated successfully`));
        if (onSuccess) {
          onSuccess();
        }
        setSubmittingForm(false);
      })
      .catch((e) => {
        console.error(e);
        const message = e.toString().replace(/^Error: /, "");
        toast.error(
          `Error ${creating ? "creating" : "editing"} formula: ${message}`,
        );
        setSubmittingForm(false);
      });
  };

  return (
    <Form>
      <Form.Group className="mb-2">
        <Form.Label>Identifier</Form.Label>
        <Form.Control
          type="text"
          value={formState.sourceIdentifier}
          placeholder={gettext("Enter an identifier for the datapoints")}
          onChange={(e) => {
            setFormState({
              ...formState,
              sourceIdentifier: e.target.value,
            });
          }}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          value={formState.name}
          placeholder={gettext("Enter a name for the datapoints")}
          onChange={(e) => {
            setFormState({
              ...formState,
              name: e.target.value,
            });
          }}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>System Name</Form.Label>
        <Form.Control
          type="text"
          value={formState.systemName}
          placeholder={gettext(
            "Enter a machine-readable name for the datapoints",
          )}
          onChange={(e) => {
            setFormState({
              ...formState,
              systemName: e.target.value,
            });
          }}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Unit</Form.Label>
        <div>
          <Form.Select
            required
            aria-label="Select a unit for the formula"
            onChange={(e) => {
              const unitId: number | undefined = e.target.value
                ? Number(e.target.value)
                : undefined;
              setFormState({
                ...formState,
                unitId,
              });
            }}
            value={formState.unitId}
          >
            <option value="">(unassigned)</option>
            {sortedUnits.map((unit: GetUnits_unit) => (
              <option key={`unit_${unit.id}`} value={unit.id}>
                {unit.symbol || gettext("(no unit)")}
              </option>
            ))}
          </Form.Select>
        </div>
      </Form.Group>
      <div className="pt-4">
        <Button
          type="button"
          variant="primary"
          disabled={submitDisabled}
          onClick={(e) => {
            e.preventDefault();
            onSave();
          }}
        >
          {creating
            ? gettext("Create manual/lab data")
            : gettext("Save changes")}
        </Button>
      </div>
    </Form>
  );
};

export default MeasurementStationManualDatapointsPage;
